import React, { useState } from "react";
import { Check, ChevronDown, X, AlertCircle, Eye, EyeOff, Search } from "lucide-react";
import Select from "react-select";

type FormField = {
  id: string;
  label: string;
  type:
    | "text"
    | "email"
    | "password"
    | "select"
    | "checkbox"
    | "textarea"
    | "number"
    | "date"
    | "file"
    | "multiselect"
    | "searchselect"
    | "custom";
  placeholder?: string;
  previewUrl?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
  validation?: {
    pattern?: RegExp;
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    customValidator?: (value: string) => boolean;
  };
  defaultValue?: string | boolean | number;
  colSpan?: number;
  loading?: boolean;
  onFileChange?: (file: File) => void;
  currentImage?: string;
  uploadedImage?: string;
  accept?: string;
  // Props for searchselect
  onSearchChange?: (value: string) => void;
  onOptionSelect?: (value: string, label: string) => void;
  selectedValue?: { value: string; label: string } | null;
  // Props for custom type
  render?: React.ReactNode;
};

type FormProps = {
  fields: FormField[];
  onSubmit: (data: Record<string, any>) => void;
  submitText?: string;
  className?: string;
  loading?: boolean;
  onFieldChange?: (fieldId: string, value: any) => void; 
};

const DynamicForm: React.FC<FormProps> = ({
  fields,
  onSubmit,
  submitText = "Submit",
  className = "",
  loading = false,
  onFieldChange,
}) => {
  const initialValues = fields.reduce((acc, field) => {
    acc[field.id] =
      field.defaultValue || (field.type === "checkbox" ? false : "");
    return acc;
  }, {} as Record<string, any>);

  const [formData, setFormData] = useState<Record<string, any>>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showPassword, setShowPassword] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filePreviews, setFilePreviews] = useState<Record<string, string>>({});
  const [searchValues, setSearchValues] = useState<Record<string, string>>({});

  const handleChange = (fieldId: string, value: any, isFile = false) => {
    const field = fields.find((f) => f.id === fieldId);
    
    if (isFile && field?.type === "file") {
      const file = value?.target?.files?.[0];
      if (file) {
        // Create preview for the selected file
        const previewUrl = URL.createObjectURL(file);
        setFilePreviews(prev => ({ ...prev, [fieldId]: previewUrl }));
        
        // Set the file in form data
        setFormData((prev) => ({
          ...prev,
          [fieldId]: file,
        }));

        // Immediately call onFileChange callback if provided
        if (field.onFileChange) {
          console.log('📤 Calling onFileChange callback with file:', file.name);
          field.onFileChange(file);
        }
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [fieldId]: value,
      }));
       if (onFieldChange) {
        console.log(`📤 Notifying parent: ${fieldId} = ${value}`);
        onFieldChange(fieldId, value);
      }
    }

    if (touched[fieldId]) {
      validateField(fieldId, isFile ? value?.target?.files?.[0] : value);
    }
  };

  const handleSearchChange = (fieldId: string, value: string) => {
    setSearchValues(prev => ({ ...prev, [fieldId]: value }));
    
    // Call the onSearchChange callback if provided
    const field = fields.find((f) => f.id === fieldId);
    if (field?.onSearchChange) {
      field.onSearchChange(value);
    }
  };

  const handleOptionSelect = (fieldId: string, value: string, label: string) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
    
    // Clear search value and show selected value
    setSearchValues(prev => ({ ...prev, [fieldId]: label }));
    
    // Call the onOptionSelect callback if provided
    const field = fields.find((f) => f.id === fieldId);
    if (field?.onOptionSelect) {
      field.onOptionSelect(value, label);
    }

    // Validate the field
    if (touched[fieldId]) {
      validateField(fieldId, value);
    }
  };

  const handleBlur = (fieldId: string) => {
    setTouched((prev) => ({ ...prev, [fieldId]: true }));
    validateField(fieldId, formData[fieldId]);
  };

  const validateField = (fieldId: string, value: any) => {
    const field = fields.find((f) => f.id === fieldId);
    if (!field) return;

    const newErrors = { ...errors };
    let errorMessage = "";

    if (field.required && !value && value !== false) {
      errorMessage = "Required";
    } else if (value && field.validation) {
      if (field.validation.pattern && !field.validation.pattern.test(value)) {
        errorMessage = "Invalid format";
      } else if (
        field.validation.minLength &&
        value.length < field.validation.minLength
      ) {
        errorMessage = `Min ${field.validation.minLength} chars`;
      } else if (
        field.validation.maxLength &&
        value.length > field.validation.maxLength
      ) {
        errorMessage = `Max ${field.validation.maxLength} chars`;
      } else if (
        field.validation.min &&
        typeof value === "number" &&
        value < field.validation.min
      ) {
        errorMessage = `Min ${field.validation.min}`;
      } else if (
        field.validation.max &&
        typeof value === "number" &&
        value > field.validation.max
      ) {
        errorMessage = `Max ${field.validation.max}`;
      } else if (
        field.validation.customValidator &&
        !field.validation.customValidator(value)
      ) {
        errorMessage = "Invalid";
      }
    }

    if (errorMessage) {
      newErrors[fieldId] = errorMessage;
    } else {
      delete newErrors[fieldId];
    }

    setErrors(newErrors);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const newTouched = { ...touched };
    fields.forEach((field) => {
      newTouched[field.id] = true;
      validateField(field.id, formData[field.id]);
    });
    setTouched(newTouched);

    await new Promise((resolve) => setTimeout(resolve, 100));

    if (Object.keys(errors).length === 0) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      onSubmit(formData);
    }

    setIsSubmitting(false);
  };

  const togglePasswordVisibility = (fieldId: string) => {
    setShowPassword((prev) => ({ ...prev, [fieldId]: !prev[fieldId] }));
  };

  const renderField = (field: FormField) => {
    // For custom type, directly render the provided content
    if (field.type === "custom" && field.render) {
      return field.render;
    }

    const isInvalid = !!errors[field.id] && touched[field.id];
    const isValid =
      touched[field.id] && !errors[field.id] && formData[field.id];

    const inputClasses = `
      w-full px-3 py-2 text-sm border rounded-md transition-colors
      focus:outline-none focus:ring-1
      ${
        isInvalid
          ? "border-red-300 focus:border-red-500 focus:ring-red-200"
          : isValid
          ? "border-green-300 focus:border-green-500 focus:ring-green-200"
          : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
      }
      ${field.loading ? "opacity-50 cursor-not-allowed" : ""}
    `;

    const commonProps = {
      id: field.id,
      name: field.id,
      value: field.type === "checkbox" || field.type === "file" || field.type === "searchselect" ? undefined : formData[field.id],
      checked: field.type === "checkbox" ? formData[field.id] : undefined,
      onChange: (
        e: React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      ) => {
        const value =
          field.type === "checkbox"
            ? (e as React.ChangeEvent<HTMLInputElement>).target.checked
            : e.target.value;
        handleChange(field.id, value);
      },
      onBlur: () => handleBlur(field.id),
      placeholder: field.placeholder,
      disabled: field.loading,
    };

    switch (field.type) {
      case "text":
      case "email":
      case "number":
      case "date":
        return (
          <div className="relative">
            <input
              type={field.type}
              {...commonProps}
              className={inputClasses}
            />
            {isValid && (
              <Check className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
            )}
          </div>
        );

      case "password":
        return (
          <div className="relative">
            <input
              type={showPassword[field.id] ? "text" : "password"}
              {...commonProps}
              className={inputClasses}
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility(field.id)}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword[field.id] ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
            {isValid && !showPassword[field.id] && (
              <Check className="absolute right-8 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
            )}
          </div>
        );

      case "textarea":
        return (
          <div className="relative">
            <textarea
              {...commonProps}
              rows={3}
              className={`${inputClasses} resize-none`}
            />
            {isValid && (
              <Check className="absolute right-2 top-2 h-4 w-4 text-green-500" />
            )}
          </div>
        );

      case "select":
        return (
          <div className="relative">
            <select
              {...commonProps}
              className={`${inputClasses} appearance-none`}
            >
              {field.placeholder && !field.defaultValue && (
                <option value="" disabled>
                  {field.placeholder}
                </option>
              )}
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            {isValid && (
              <Check className="absolute right-8 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
            )}
          </div>
        );

      case "checkbox":
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              {...commonProps}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label
              htmlFor={field.id}
              className="ml-2 text-sm text-gray-700 text-[10px]"
            >
              {field.label}
            </label>
          </div>
        );

      case "file":
        return (
          <div className="space-y-3">
            {/* Show loading state */}
            {field.loading && (
              <div className="flex items-center text-sm text-blue-600">
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                Uploading image...
              </div>
            )}

            {/* Preview for existing image (currentImage) */}
            {field.currentImage && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Current Image:</p>
                <img
                  src={field.currentImage}
                  alt={`Current ${field.label}`}
                  className="w-32 h-32 object-contain border rounded bg-gray-50"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}

            {/* Preview for newly uploaded image (uploadedImage) */}
            {field.uploadedImage && (
              <div>
                <p className="text-sm text-green-600 mb-1">Uploaded Image:</p>
                <img
                  src={field.uploadedImage}
                  alt={`Uploaded ${field.label}`}
                  className="w-32 h-32 object-contain border rounded bg-green-50"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}

            {/* Preview for selected file (before upload) */}
            {filePreviews[field.id] && !field.uploadedImage && (
              <div>
                <p className="text-sm text-blue-600 mb-1">Selected Image Preview:</p>
                <img
                  src={filePreviews[field.id]}
                  alt={`Selected ${field.label} preview`}
                  className="w-32 h-32 object-contain border rounded"
                />
              </div>
            )}

            {/* File input */}
            <div className="relative">
              <input
                type="file"
                id={field.id}
                name={field.id}
                onChange={(e) => handleChange(field.id, e, true)}
                className={inputClasses}
                accept={field.accept || "image/*"}
                disabled={field.loading}
              />
              {isValid && (
                <Check className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
              )}
            </div>

            {/* Helper text */}
            <p className="text-xs text-gray-500">
              {field.uploadedImage 
                ? "Image uploaded successfully! Click submit to save."
                : "Select an image to upload immediately"}
            </p>
          </div>
        );

      case "multiselect":
        return (
          <Select
            isMulti
            name={field.id}
            value={formData[field.id]}
            onChange={(selectedOptions) =>
              handleChange(field.id, selectedOptions)
            }
            onBlur={() => handleBlur(field.id)}
            options={field.options}
            className="react-select-container"
            classNamePrefix="react-select"
            placeholder={field.placeholder}
            isDisabled={field.loading}
          />
        );

      case "searchselect":
        return (
          <div className="space-y-2">
            <div className="relative">
              <input
                type="text"
                value={searchValues[field.id] || field.selectedValue?.label || ""}
                onChange={(e) => handleSearchChange(field.id, e.target.value)}
                onBlur={() => handleBlur(field.id)}
                placeholder={field.placeholder || "Type to search..."}
                className={inputClasses}
                disabled={field.loading}
              />
              <Search className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              {isValid && (
                <Check className="absolute right-8 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
              )}
            </div>
            
            {/* Dropdown options */}
            {field.options && field.options.length > 0 && searchValues[field.id] && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {field.options.map((option) => (
                  <div
                    key={option.value}
                    className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    onClick={() => handleOptionSelect(field.id, option.value, option.label)}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}

            {/* Loading state */}
            {field.loading && (
              <div className="flex items-center text-xs text-blue-600">
                <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                Searching...
              </div>
            )}

            {/* No results state */}
            {field.options && field.options.length === 0 && searchValues[field.id] && !field.loading && (
              <div className="text-xs text-gray-500 px-2">
                No results found
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {fields.map((field) => (
          <div
            key={field.id}
            className={`${field.colSpan === 2 ? "sm:col-span-2" : ""} ${
              field.type === "searchselect" ? "relative" : ""
            }`}
          >
            {field.type !== "checkbox" && (
              <label htmlFor={field.id} className="text-[10px] mb-1">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}

            {renderField(field)}  

            {errors[field.id] && touched[field.id] && (
              <div className="flex items-center mt-1 text-xs text-red-600">
                <AlertCircle className="h-3 w-3 mr-1" />
                <span>{errors[field.id]}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={hasErrors || isSubmitting || loading}
        className={`
          w-full py-2 px-4 text-sm font-medium rounded-md transition-colors
          focus:outline-none focus:ring-2 focus:ring-offset-2
          ${
            hasErrors || isSubmitting || loading
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500"
          }
        `}
      >
        {isSubmitting || loading ? (
          <div className="flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
            {loading ? "Uploading..." : "Loading..."}
          </div>
        ) : (
          submitText
        )}
      </button>
    </div>
  );
};

export default DynamicForm;

// import React, { useState } from "react";
// import { Check, ChevronDown, X, AlertCircle, Eye, EyeOff } from "lucide-react";
// import Select from "react-select";

// type FormField = {
//   id: string;
//   label: string;
//   type:
//     | "text"
//     | "email"
//     | "password"
//     | "select"
//     | "checkbox"
//     | "textarea"
//     | "number"
//     | "date"
//     | "file"
//     | "multiselect";
//   placeholder?: string;
//   previewUrl?:string;
//   required?: boolean;
//   options?: { value: string; label: string }[];
//   validation?: {
//     pattern?: RegExp;
//     minLength?: number;
//     maxLength?: number;
//     min?: number;
//     max?: number;
//     customValidator?: (value: string) => boolean;
//   };
//   defaultValue?: string | boolean | number;
//   colSpan?: number;
// };

// type FormProps = {
//   fields: FormField[];
//   onSubmit: (data: Record<string, any>) => void;
//   submitText?: string;
//   className?: string;
// };

// const DynamicForm: React.FC<FormProps> = ({
//   fields,
//   onSubmit,
//   submitText = "Submit",
//   className = "",
// }) => {
//   const initialValues = fields.reduce((acc, field) => {
//     acc[field.id] =
//       field.defaultValue || (field.type === "checkbox" ? false : "");
//     return acc;
//   }, {} as Record<string, any>);

//   const [formData, setFormData] = useState<Record<string, any>>(initialValues);
//   const [errors, setErrors] = useState<Record<string, string>>({});
//   const [touched, setTouched] = useState<Record<string, boolean>>({});
//   const [showPassword, setShowPassword] = useState<Record<string, boolean>>({});
//   const [isSubmitting, setIsSubmitting] = useState(false);

//   const handleChange = (fieldId: string, value: any, isFile = false) => {
//     setFormData((prev) => ({
//       ...prev,
//       [fieldId]: isFile ? value?.target?.files : value,
//     }));
//     if (touched[fieldId]) {
//       validateField(fieldId, isFile ? value?.target?.files : value);
//     }
//   };

//   const handleBlur = (fieldId: string) => {
//     setTouched((prev) => ({ ...prev, [fieldId]: true }));
//     validateField(fieldId, formData[fieldId]);
//   };

//   const validateField = (fieldId: string, value: any) => {
//     const field = fields.find((f) => f.id === fieldId);
//     if (!field) return;

//     const newErrors = { ...errors };
//     let errorMessage = "";

//     if (field.required && !value && value !== false) {
//       errorMessage = "Required";
//     } else if (value && field.validation) {
//       if (field.validation.pattern && !field.validation.pattern.test(value)) {
//         errorMessage = "Invalid format";
//       } else if (
//         field.validation.minLength &&
//         value.length < field.validation.minLength
//       ) {
//         errorMessage = `Min ${field.validation.minLength} chars`;
//       } else if (
//         field.validation.maxLength &&
//         value.length > field.validation.maxLength
//       ) {
//         errorMessage = `Max ${field.validation.maxLength} chars`;
//       } else if (
//         field.validation.min &&
//         typeof value === "number" &&
//         value < field.validation.min
//       ) {
//         errorMessage = `Min ${field.validation.min}`;
//       } else if (
//         field.validation.max &&
//         typeof value === "number" &&
//         value > field.validation.max
//       ) {
//         errorMessage = `Max ${field.validation.max}`;
//       } else if (
//         field.validation.customValidator &&
//         !field.validation.customValidator(value)
//       ) {
//         errorMessage = "Invalid";
//       }
//     }

//     if (errorMessage) {
//       newErrors[fieldId] = errorMessage;
//     } else {
//       delete newErrors[fieldId];
//     }

//     setErrors(newErrors);
//   };

//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();
//     setIsSubmitting(true);

//     const newTouched = { ...touched };
//     fields.forEach((field) => {
//       newTouched[field.id] = true;
//       validateField(field.id, formData[field.id]);
//     });
//     setTouched(newTouched);

//     await new Promise((resolve) => setTimeout(resolve, 100));

//     if (Object.keys(errors).length === 0) {
//       await new Promise((resolve) => setTimeout(resolve, 500));
//       onSubmit(formData);
//     }

//     setIsSubmitting(false);
//   };

//   const togglePasswordVisibility = (fieldId: string) => {
//     setShowPassword((prev) => ({ ...prev, [fieldId]: !prev[fieldId] }));
//   };

//   const renderField = (field: FormField) => {
//     const isInvalid = !!errors[field.id] && touched[field.id];
//     const isValid =
//       touched[field.id] && !errors[field.id] && formData[field.id];

//     const inputClasses = `
//       w-full px-3 py-2 text-sm border rounded-md transition-colors
//       focus:outline-none focus:ring-1
//       ${
//         isInvalid
//           ? "border-red-300 focus:border-red-500 focus:ring-red-200"
//           : isValid
//           ? "border-green-300 focus:border-green-500 focus:ring-green-200"
//           : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
//       }
//     `;

//     const commonProps = {
//       id: field.id,
//       name: field.id,
//       value: field.type === "checkbox" ? undefined : formData[field.id],
//       checked: field.type === "checkbox" ? formData[field.id] : undefined,
//       onChange: (
//         e: React.ChangeEvent<
//           HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
//         >
//       ) => {
//         const value =
//           field.type === "checkbox"
//             ? (e as React.ChangeEvent<HTMLInputElement>).target.checked
//             : e.target.value;
//         handleChange(field.id, value);
//       },
//       onBlur: () => handleBlur(field.id),
//       placeholder: field.placeholder,
//     };

//     switch (field.type) {
//       case "text":
//       case "email":
//       case "number":
//       case "date":
//         return (
//           <div className="relative">
//             <input
//               type={field.type}
//               {...commonProps}
//               className={inputClasses}
//             />
//             {isValid && (
//               <Check className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
//             )}
//           </div>
//         );

//       case "password":
//         return (
//           <div className="relative">
//             <input
//               type={showPassword[field.id] ? "text" : "password"}
//               {...commonProps}
//               className={inputClasses}
//             />
//             <button
//               type="button"
//               onClick={() => togglePasswordVisibility(field.id)}
//               className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
//             >
//               {showPassword[field.id] ? (
//                 <EyeOff className="h-4 w-4" />
//               ) : (
//                 <Eye className="h-4 w-4" />
//               )}
//             </button>
//             {isValid && !showPassword[field.id] && (
//               <Check className="absolute right-8 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
//             )}
//           </div>
//         );

//       case "textarea":
//         return (
//           <div className="relative">
//             <textarea
//               {...commonProps}
//               rows={3}
//               className={`${inputClasses} resize-none`}
//             />
//             {isValid && (
//               <Check className="absolute right-2 top-2 h-4 w-4 text-green-500" />
//             )}
//           </div>
//         );

//       case "select":
//         return (
//           <div className="relative">
//             <select
//               {...commonProps}
//               className={`${inputClasses} appearance-none`}
//             >
//               {field.placeholder && !field.defaultValue && (
//                 <option value="" disabled>
//                   {field.placeholder}
//                 </option>
//               )}
//               {field.options?.map((option) => (
//                 <option key={option.value} value={option.value}>
//                   {option.label}
//                 </option>
//               ))}
//             </select>
//             <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
//             {isValid && (
//               <Check className="absolute right-8 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
//             )}
//           </div>
//         );

//       case "checkbox":
//         return (
//           <div className="flex items-center">
//             <input
//               type="checkbox"
//               {...commonProps}
//               className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
//             />
//             <label
//               htmlFor={field.id}
//               className="ml-2 text-sm text-gray-700 text-[10px]"
//             >
//               {field.label}
//             </label>
//           </div>
//         );
//       // case "file":
//       //   return (
//       //     <input
//       //       type="file"
//       //       id={field.id}
//       //       name={field.id}
//       //       onChange={(e) => handleChange(field.id, e, true)}

//       //       className={inputClasses}
//       //       accept="image/*"
//       //     />
//       //   );

//       case "file":
//         return (
//           <div className="space-y-2">
//             {/* Preview for existing image */}
//             {field.previewUrl && (
//               <div className="mb-2">
//                 <p className="text-sm text-gray-600 mb-1">Current Image:</p>
//                 <img
//                   src={field.previewUrl}
//                   alt={`Current ${field.label}`}
//                   className="w-32 h-32 object-contain border rounded bg-gray-50"
//                 />
//                 <p className="text-xs text-gray-500 mt-1">
//                   Upload new image to replace the current one
//                 </p>
//               </div>
//             )}

//             {/* File input */}
//             <input
//               type="file"
//               id={field.id}
//               name={field.id}
//               onChange={(e) => handleChange(field.id, e, true)}
//               className={inputClasses}
//               accept="image/*"
//             />

//             {/* Preview for newly selected file */}
//             {formData[field.id]?.preview && (
//               <div className="mt-2">
//                 <p className="text-sm text-gray-600 mb-1">New Image Preview:</p>
//                 <img
//                   src={formData[field.id].preview}
//                   alt={`New ${field.label} preview`}
//                   className="w-32 h-32 object-contain border rounded"
//                 />
//               </div>
//             )}
//           </div>
//         );
//       case "multiselect":
//         return (
//           <Select
//             isMulti
//             name={field.id}
//             value={formData[field.id]}
//             onChange={(selectedOptions) =>
//               handleChange(field.id, selectedOptions)
//             }
//             onBlur={() => handleBlur(field.id)}
//             options={field.options}
//             className="react-select-container"
//             classNamePrefix="react-select"
//             placeholder={field.placeholder}
//           />
//         );
//       default:
//         return null;
//     }
//   };

//   const hasErrors = Object.keys(errors).length > 0;

//   return (
//     <div className={`space-y-4 ${className}`}>
//       <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
//         {fields.map((field) => (
//           <div
//             key={field.id}
//             className={field.colSpan === 2 ? "sm:col-span-2" : ""}
//           >
//             {field.type !== "checkbox" && (
//               <label htmlFor={field.id} className="text-[10px] mb-1">
//                 {field.label}
//                 {field.required && <span className="text-red-500 ml-1">*</span>}
//               </label>
//             )}

//             {renderField(field)}  

//             {errors[field.id] && touched[field.id] && (
//               <div className="flex items-center mt-1 text-xs text-red-600">
//                 <AlertCircle className="h-3 w-3 mr-1" />
//                 <span>{errors[field.id]}</span>
//               </div>
//             )}
//           </div>
//         ))}
//       </div>

//       <button
//         type="button"
//         onClick={handleSubmit}
//         disabled={hasErrors || isSubmitting}
//         className={`
//           w-full py-2 px-4 text-sm font-medium rounded-md transition-colors
//           focus:outline-none focus:ring-2 focus:ring-offset-2
//           ${
//             hasErrors || isSubmitting
//               ? "bg-gray-300 text-gray-500 cursor-not-allowed"
//               : "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500"
//           }
//         `}
//       >
//         {isSubmitting ? (
//           <div className="flex items-center justify-center">
//             <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
//             Loading...
//           </div>
//         ) : (
//           submitText
//         )}
//       </button>
//     </div>
//   );
// };

// export default DynamicForm;
